import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  LoaderSize,
  LoaderThemeColor,
  LoaderType,
} from '@progress/kendo-angular-indicators';
import { Subscription, firstValueFrom } from 'rxjs';
import { OnboardingModuleStatuses } from 'src/app/core/enums/onboarding-module-status.enum';
import { IDynamicHeightComponent } from 'src/app/core/interfaces/common/ui/dynamic-height-component.interface';
import { PageState } from 'src/app/core/models/page-state/page-state.model';
import { PageStateService } from 'src/app/core/services/onboarding/page-state.service';
import { IAppState } from 'src/app/core/store/common/interfaces/app-state.interface';
import { IEnrollmentStore } from 'src/app/core/store/onboarding/interfaces/enrollment-store.interface';
import {
  selectAllEnrollment,
  selectHasClientAdditionalInfoRequest,
  selectModuleStatuses,
} from 'src/app/core/store/onboarding/reducers+selectors/enrollment.selector';
import { destructSubscriptionArray } from 'src/app/core/ui-helpers/functions/destruct-subscription-array';

interface IIcon {
  name: string;
  src: string;
  stateId: string;
  imagePath: string;
  imagePathHover: string;
  disabled: boolean;
  selected: boolean;
  recentlySelected: boolean;
}

@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
})
export class SidenavComponent
  implements OnInit, IDynamicHeightComponent, OnDestroy
{
  @Input() isMobileOpen: boolean;
  @Input() homeState: PageState; // input will override default state assignment
  @Input() bpState: PageState; // input will override default state assignment
  @Input() fpState: PageState; // input will override default state assignment
  @Input() cpState: PageState; // input will override default state assignment
  @Input() disableDocuments = false;
  @Input() openMobile: () => void;
  @Input() closeMobile: () => void;
  @ViewChild('sidenav', { static: true }) sidenav: ElementRef;
  @ViewChild('mobileSidenav', { static: true }) mobileSidenav: ElementRef;
  selected = false;
  logoPath = '../../../assets/images/Now_Flower_Logo.png';
  icons: IIcon[];
  subscriptions: Subscription[] = [];

  loader = {
    type: <LoaderType>'converging-spinner',
    themeColor: <LoaderThemeColor>'tertiary',
    size: <LoaderSize>'medium',
  };

  setHeight(height: number): void {
    if (!this.sidenav || !this.mobileSidenav) return;
    this.sidenav.nativeElement.style.height = `${height}px`;
    this.mobileSidenav.nativeElement.style.height = `${height}px`;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ps: PageStateService,
    private store: Store<IAppState>
  ) {}

  async ngOnInit(): Promise<void> {
    this.disableDocuments = this.disableDocuments
      ? this.disableDocuments
      : await this.overrideDisableDocuments();

    this.subscriptions.push(
      this.route.data.subscribe(async (data) => {
        this.subscriptions.push(
          this.route.queryParams.subscribe(async (params) => {
            const stateId = params['stateId'].toString().trim();
            this.populatePageStates();
            this.icons = [
              {
                name: 'Home',
                src:
                  this.homeState === undefined ? '#' : this.homeState.pageUrl,
                stateId: this.homeState === undefined ? '0' : this.homeState.id,
                imagePath: '../../../assets/images/NoBg-Icons/Home(White).png',
                imagePathHover:
                  '../../../assets/images/NoBg-Icons/Home(Green).png',
                disabled: this.homeState === undefined,
                selected:
                  this.homeState !== undefined &&
                  this.homeState.id.toString().trim() === stateId,
                recentlySelected: false,
              },
              {
                name: 'Business Profile',
                src: this.bpState === undefined ? '#' : this.bpState.pageUrl,
                stateId: this.bpState === undefined ? '0' : this.bpState.id,
                imagePath:
                  '../../../assets/images/NoBg-Icons/Business_Profile(White).png',
                imagePathHover:
                  '../../../assets/images/NoBg-Icons/Business_Profile(Green).png',
                disabled: this.bpState === undefined || this.bpState.disabled,
                selected:
                  this.bpState !== undefined &&
                  this.bpState.id.toString().trim() === stateId,
                recentlySelected: false,
              },
              {
                name: 'Financial Details',
                src: this.fpState === undefined ? '#' : this.fpState.pageUrl,
                stateId: this.fpState === undefined ? '0' : this.fpState.id,
                imagePath:
                  '../../../assets/images/NoBg-Icons/Financial_Profile(White).png',
                imagePathHover:
                  '../../../assets/images/NoBg-Icons/Financial_Profile(Green).png',
                disabled: this.fpState === undefined || this.fpState.disabled,
                selected:
                  this.fpState !== undefined &&
                  this.fpState.id.toString().trim() === stateId,
                recentlySelected: false,
              },
              {
                name: 'Customer Portfolio',
                src: this.cpState === undefined ? '#' : this.cpState.pageUrl,
                stateId: this.cpState === undefined ? '0' : this.cpState.id,
                imagePath:
                  '../../../assets/images/NoBg-Icons/Customer_Portfolio(White).png',
                imagePathHover:
                  '../../../assets/images/NoBg-Icons/Customer_Portfolio(Green).png',
                disabled: this.cpState === undefined || this.cpState.disabled,
                selected:
                  this.cpState !== undefined &&
                  this.cpState.id.toString().trim() === stateId,
                recentlySelected: false,
              },
              {
                name: 'Documents',
                src: '../../onboarding/documents',
                stateId: '0',
                imagePath:
                  '../../../assets/images/NoBg-Icons/Documents(White).png',
                imagePathHover:
                  '../../../assets/images/NoBg-Icons/Documents(Green).png',
                disabled: this.disableDocuments,
                selected: stateId === '0',
                recentlySelected: false,
              },
            ];
          })
        );
      })
    );
  }

  ngOnDestroy(): void {
    destructSubscriptionArray(this.subscriptions);
  }

  populatePageStates() {
    const props$ = this.store.select(selectAllEnrollment);
    this.subscriptions.push(
      props$.subscribe((props: IEnrollmentStore) => {
        if (
          (this.homeState === undefined || this.homeState.id === '-1') &&
          props.dashboardStateId
        )
          this.homeState = this.ps.getPageStateById(
            props.dashboardStateId.toString()
          );
        if (
          (this.bpState === undefined || this.bpState.id === '-1') &&
          props.businessProfileStateId
        )
          this.bpState = this.ps.getPageStateById(
            props.businessProfileStateId.toString()
          );
        if (
          (this.fpState === undefined || this.fpState.id === '-1') &&
          props.financialProfileStateId
        )
          this.fpState = this.ps.getPageStateById(
            props.financialProfileStateId.toString()
          );
        if (
          (this.cpState === undefined || this.cpState.id === '-1') &&
          props.customerPortfolioStateId
        )
          this.cpState = this.ps.getPageStateById(
            props.customerPortfolioStateId.toString()
          );
      })
    );
  }

  async overrideDisableDocuments(): Promise<boolean> {
    const props = await firstValueFrom(this.store.select(selectModuleStatuses));
    return (
      props?.businessProfileStatusId !== OnboardingModuleStatuses.COMPLETED
    );
  }

  async navigate(icon: IIcon) {
    // eslint-disable-next-line no-param-reassign
    icon.recentlySelected = true;
    setTimeout(() => {
      // eslint-disable-next-line no-param-reassign
      icon.recentlySelected = false;
    }, 3000);
    if (
      this.getHasClientAdditionalInfoRequest() &&
      icon.stateId === this.homeState.id
    ) {
      const tempState =
        await this.ps.getPageState_ClientAdditionalInfoRequestDashboard();
      await this.router.navigate([tempState.pageUrl], {
        queryParams: { stateId: tempState.id },
      });
      return;
    }
    await this.router.navigate([icon.src], {
      queryParams: { stateId: icon.stateId },
    });
  }

  getHasClientAdditionalInfoRequest(): boolean {
    const hasClientAdditionalInfoRequest$ = this.store.select(
      selectHasClientAdditionalInfoRequest
    );
    let hasClientAdditionalInfoRequest = false;
    const sub = hasClientAdditionalInfoRequest$.subscribe((prop) => {
      hasClientAdditionalInfoRequest = prop;
    });
    sub.unsubscribe();
    return hasClientAdditionalInfoRequest;
  }
}
